import React, { useEffect, useState } from "react";
import { ArrowUpRight, Star } from "lucide-react";
import { MEDIA } from "../../lib/constants";

const HERO_SLIDES = [
  {
    src: MEDIA.heroInterior,
    alt: "Inside District Wellness Center — DWC lobby at 4915 Georgia Ave NW, Washington DC",
  },
  {
    src: MEDIA.storefrontDoor,
    alt: "Customer entering District Wellness Center at 4915 Georgia Ave NW, Washington DC",
  },
  {
    src: MEDIA.khalifaKush,
    alt: "Khalifa Kush cannabis pre-roll and Khalifa Mints flower jar at District Wellness Center",
  },
  {
    src: MEDIA.altSolJars,
    alt: "Alt Sol Cannabis flower jars — Duct Tape, Blue Sharpies, Hashburger — at District Wellness Center",
  },
];

export const Hero = () => {
  const [slide, setSlide] = useState(0);

  useEffect(() => {
    const id = setInterval(() => {
      setSlide((i) => (i + 1) % HERO_SLIDES.length);
    }, 4500);
    return () => clearInterval(id);
  }, []);

  return (
    <section
      className="relative overflow-hidden bg-[#FAFAF7]"
      data-testid="hero"
    >
      {/* Soft warm color wash — barely-there */}
      <div className="absolute -top-40 -right-40 w-[600px] h-[600px] bg-gradient-radial from-brand/[0.06] via-brand/[0.02] to-transparent blur-3xl pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-6 pt-16 sm:pt-24 pb-20 sm:pb-32 grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
        {/* LEFT: copy */}
        <div className="lg:col-span-7 relative z-10">
          <div className="flex items-center gap-3 mb-8 text-[10px] uppercase tracking-[0.35em] text-ink-muted">
            <span className="w-8 h-px bg-brand" />
            <span>Now Open · Georgia Ave NW</span>
          </div>

          <h1 className="font-serif text-[48px] sm:text-6xl lg:text-7xl xl:text-[92px] leading-[0.92] tracking-tight text-ink">
            Trusted <em className="text-brand not-italic font-serif italic">medical cannabis</em><br />
            dispensary in D.C.
          </h1>

          <p className="mt-8 text-base sm:text-lg text-ink-muted max-w-lg leading-relaxed">
            A licensed, patient-first dispensary on Georgia Ave NW — premium flower,
            edibles, vapes and same-day DC delivery.
          </p>

          <div className="mt-10 flex flex-wrap items-center gap-6">
            <a
              href="/menu"
              className="inline-flex items-center gap-2 bg-ink hover:bg-brand text-white px-8 py-4 text-[11px] uppercase tracking-[0.25em] font-medium transition-colors"
              data-testid="hero-order-online-btn"
            >
              Shop the menu
              <ArrowUpRight size={14} />
            </a>
            <a
              href="#medical-access"
              className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] text-ink border-b border-ink/30 hover:border-brand hover:text-brand pb-1 transition-colors"
              data-testid="hero-register-btn"
            >
              Register as a patient
              <ArrowUpRight size={12} />
            </a>
          </div>
        </div>

        {/* RIGHT: rotating hero slideshow (store + featured products) */}
        <div className="lg:col-span-5 relative">
          <div className="relative">
            <div
              className="relative w-full h-[440px] sm:h-[560px] overflow-hidden bg-ink/5"
              data-testid="hero-slideshow"
            >
              {HERO_SLIDES.map((s, i) => (
                <img
                  key={s.src}
                  src={s.src}
                  alt={s.alt}
                  loading={i === 0 ? "eager" : "lazy"}
                  className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ease-in-out ${
                    i === slide ? "opacity-100" : "opacity-0"
                  }`}
                  data-testid={`hero-slide-${i}`}
                />
              ))}

              {/* Slide indicators */}
              <div
                className="absolute top-5 right-5 flex items-center gap-1.5 z-10"
                data-testid="hero-slide-indicators"
              >
                {HERO_SLIDES.map((_, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => setSlide(i)}
                    aria-label={`Show slide ${i + 1}`}
                    className={`h-1 rounded-full transition-all duration-300 ${
                      i === slide
                        ? "w-6 bg-white"
                        : "w-3 bg-white/50 hover:bg-white/80"
                    }`}
                    data-testid={`hero-slide-dot-${i}`}
                  />
                ))}
              </div>
            </div>

            {/* Subtle bottom-left address plate — single tidy label */}
            <div
              className="absolute bottom-5 left-5 right-5 sm:right-auto sm:max-w-[280px] bg-white/95 backdrop-blur-sm p-5 shadow-[0_10px_40px_-10px_rgba(0,0,0,0.15)]"
              data-testid="hero-address-plate"
            >
              <div className="flex items-center gap-1.5 text-brand text-xs mb-2">
                <Star size={12} fill="currentColor" strokeWidth={0} />
                <Star size={12} fill="currentColor" strokeWidth={0} />
                <Star size={12} fill="currentColor" strokeWidth={0} />
                <Star size={12} fill="currentColor" strokeWidth={0} />
                <Star size={12} fill="currentColor" strokeWidth={0} />
                <span className="text-[10px] uppercase tracking-[0.2em] text-ink-muted ml-1">5.0 on Google</span>
              </div>
              <div className="font-serif text-lg leading-tight text-ink">
                4915 Georgia Ave NW
              </div>
              <div className="text-[11px] uppercase tracking-[0.25em] text-ink-muted mt-2">
                Open 7 days · Until Late
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
